import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  FaultCategory,
  FaultTopic,
  CoilAlternative,
  CoilSearchResult,
  MaintenanceBankStats,
  SearchFilter,
  SearchResult
} from '../types/maintenanceBank';

// مجموعات Firebase
const CATEGORIES_COLLECTION = 'fault_categories';
const TOPICS_COLLECTION = 'fault_topics';
const COILS_COLLECTION = 'coil_alternatives';
const ACTIVITIES_COLLECTION = 'bank_activities';

/**
 * خدمات فئات الأعطال
 */
export class FaultCategoryService {
  // جلب جميع الفئات
  static async getCategories(branchId?: string): Promise<FaultCategory[]> {
    try {
      console.log('🔄 بدء جلب الفئات من Firestore:', {
        branchId,
        collection: CATEGORIES_COLLECTION
      });

      // استعلام بسيط بدون ترتيب لتجنب مشاكل الفهرس
      let q = query(collection(db, CATEGORIES_COLLECTION));

      if (branchId) {
        console.log(`🏢 تطبيق فلتر الفرع: ${branchId}`);
        q = query(q, where('branchId', '==', branchId));
      }

      console.log('📤 إرسال استعلام الفئات إلى Firestore...');
      const snapshot = await getDocs(q);

      console.log(`📥 تم استلام ${snapshot.docs.length} فئة من Firestore`);

      const categories = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`📁 معالجة فئة ${doc.id}:`, {
          name: data.name,
          order: data.order,
          branchId: data.branchId
        });

        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }) as FaultCategory[];

      // ترتيب محلي حسب الترتيب
      categories.sort((a, b) => {
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        return orderA - orderB;
      });

      console.log(`✅ تم تحويل وترتيب ${categories.length} فئة بنجاح`);
      return categories;
    } catch (error: any) {
      console.error('❌ خطأ مفصل في جلب الفئات من Firestore:', {
        error: error,
        message: error.message,
        code: error.code,
        branchId: branchId,
        collection: CATEGORIES_COLLECTION
      });
      throw new Error(`فشل في جلب الفئات: ${error.message || error.code || 'خطأ غير معروف'}`);
    }
  }

  // إضافة فئة جديدة
  static async addCategory(category: Omit<FaultCategory, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), {
        ...category,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding category:', error);
      throw new Error('فشل في إضافة الفئة');
    }
  }

  // تحديث فئة
  static async updateCategory(id: string, updates: Partial<FaultCategory>): Promise<void> {
    try {
      await updateDoc(doc(db, CATEGORIES_COLLECTION, id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('فشل في تحديث الفئة');
    }
  }

  // حذف فئة
  static async deleteCategory(id: string): Promise<void> {
    try {
      // التحقق من وجود مواضيع في هذه الفئة
      const topicsQuery = query(collection(db, TOPICS_COLLECTION), where('categoryId', '==', id));
      const topicsSnapshot = await getDocs(topicsQuery);
      
      if (!topicsSnapshot.empty) {
        throw new Error('لا يمكن حذف الفئة لأنها تحتوي على مواضيع');
      }

      await deleteDoc(doc(db, CATEGORIES_COLLECTION, id));
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
}

/**
 * خدمات مواضيع الأعطال
 */
export class FaultTopicService {
  // جلب المواضيع حسب الفئة
  static async getTopicsByCategory(categoryId: string, page = 1, pageSize = 10, publishedOnly = false): Promise<SearchResult<FaultTopic>> {
    console.log(`🔄 بدء جلب المواضيع من Firestore:`, {
      categoryId,
      page,
      pageSize,
      publishedOnly,
      collection: TOPICS_COLLECTION
    });

    // استراتيجية بسيطة: جلب جميع المواضيع للفئة وفلترتها محلي<|im_start|>
    try {
      console.log('📋 استخدام استراتيجية الجلب البسيط...');

      // استعلام بسيط جداً بدون أي ترتيب أو فهارس معقدة
      const simpleQuery = query(
        collection(db, TOPICS_COLLECTION),
        where('categoryId', '==', categoryId)
      );

      console.log('📤 إرسال استعلام بسيط إلى Firestore...');
      const snapshot = await getDocs(simpleQuery);

      console.log(`📥 تم استلام ${snapshot.docs.length} مستند من Firestore`);

      let topics = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`📄 معالجة مستند ${doc.id}:`, {
          categoryId: data.categoryId,
          title: data.title,
          isPublished: data.isPublished,
          createdAt: data.createdAt
        });

        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        };
      }) as FaultTopic[];

      // فلترة محلية للمواضيع المنشورة إذا لزم الأمر
      if (publishedOnly) {
        topics = topics.filter((topic: FaultTopic) => topic.isPublished === true);
        console.log(`📋 تم فلترة المواضيع المنشورة: ${topics.length} موضوع`);
      }

      // ترتيب محلي حسب تاريخ الإنشاء (الأحدث أولاً)
      topics.sort((a: FaultTopic, b: FaultTopic) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      console.log('🔄 تم ترتيب المواضيع محلي<|im_start|> حسب تاريخ الإنشاء');

      // تطبيق التصفح (pagination) محلي<|im_start|>
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedTopics = topics.slice(startIndex, endIndex);

      console.log(`✅ تم تحويل ${paginatedTopics.length} موضوع بنجاح (من ${topics.length} إجمالي)`);

      return {
        items: paginatedTopics,
        total: topics.length,
        page,
        pageSize,
        hasMore: endIndex < topics.length
      };


    } catch (error: any) {
      console.error('❌ خطأ مفصل في جلب المواضيع من Firestore:', {
        error: error,
        message: error.message,
        code: error.code,
        categoryId: categoryId,
        collection: TOPICS_COLLECTION
      });

      throw new Error(`فشل في جلب المواضيع: ${error.message || error.code || 'خطأ غير معروف'}`);
    }
  }

  // جلب جميع المواضيع حسب الفئة (للموظفين - بما في ذلك غير المنشورة)
  static async getAllTopicsByCategory(categoryId: string, page = 1, pageSize = 50): Promise<SearchResult<FaultTopic>> {
    return this.getTopicsByCategory(categoryId, page, pageSize, false);
  }

  // جلب المواضيع المنشورة فقط (للعرض العام)
  static async getPublishedTopicsByCategory(categoryId: string, page = 1, pageSize = 10): Promise<SearchResult<FaultTopic>> {
    return this.getTopicsByCategory(categoryId, page, pageSize, true);
  }

  // جلب موضوع واحد
  static async getTopic(id: string): Promise<FaultTopic | null> {
    try {
      const docRef = doc(db, TOPICS_COLLECTION, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      // زيادة عدد المشاهدات
      await updateDoc(docRef, {
        views: increment(1)
      });

      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data()?.createdAt?.toDate() || new Date(),
        updatedAt: docSnap.data()?.updatedAt?.toDate() || new Date()
      } as FaultTopic;
    } catch (error) {
      console.error('Error fetching topic:', error);
      throw new Error('فشل في جلب الموضوع');
    }
  }

  // إضافة موضوع جديد
  static async addTopic(topic: Omit<FaultTopic, 'id' | 'createdAt' | 'updatedAt' | 'views' | 'likes'>): Promise<string> {
    try {
      console.log('📝 إضافة موضوع جديد:', topic);
      const docRef = await addDoc(collection(db, TOPICS_COLLECTION), {
        ...topic,
        views: 0,
        likes: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log('✅ تم إضافة الموضوع بنجاح:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error adding topic:', error);
      throw new Error('فشل في إضافة الموضوع');
    }
  }



  // تحديث موضوع
  static async updateTopic(id: string, updates: Partial<FaultTopic>): Promise<void> {
    try {
      console.log('📝 تحديث الموضوع:', id, updates);
      await updateDoc(doc(db, TOPICS_COLLECTION, id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      console.log('✅ تم تحديث الموضوع بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تحديث الموضوع:', error);
      throw new Error('فشل في تحديث الموضوع');
    }
  }

  // حذف موضوع
  static async deleteTopic(id: string): Promise<void> {
    try {
      console.log('🗑️ حذف الموضوع:', id);
      await deleteDoc(doc(db, TOPICS_COLLECTION, id));
      console.log('✅ تم حذف الموضوع بنجاح');
    } catch (error) {
      console.error('❌ خطأ في حذف الموضوع:', error);
      throw new Error('فشل في حذف الموضوع');
    }
  }

  // البحث في المواضيع
  static async searchTopics(searchTerm: string, filters?: SearchFilter): Promise<SearchResult<FaultTopic>> {
    try {
      // هذا تنفيذ مبسط - يمكن تحسينه باستخدام Algolia أو Elasticsearch
      let q = query(
        collection(db, TOPICS_COLLECTION),
        where('isPublished', '==', true)
      );

      if (filters?.categories && filters.categories.length > 0) {
        q = query(q, where('categoryId', 'in', filters.categories));
      }

      const snapshot = await getDocs(q);
      let topics = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as FaultTopic[];

      // فلترة النتائج محلياً (يمكن تحسينها)
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        topics = topics.filter(topic =>
          topic.title.toLowerCase().includes(term) ||
          topic.description.toLowerCase().includes(term) ||
          topic.tags.some(tag => tag.toLowerCase().includes(term))
        );
      }

      if (filters?.difficulty && filters.difficulty.length > 0) {
        topics = topics.filter(topic => filters.difficulty!.includes(topic.difficulty));
      }

      // ترتيب النتائج
      if (filters?.sortBy) {
        topics.sort((a, b) => {
          let comparison = 0;
          switch (filters.sortBy) {
            case 'newest':
              comparison = b.createdAt.getTime() - a.createdAt.getTime();
              break;
            case 'oldest':
              comparison = a.createdAt.getTime() - b.createdAt.getTime();
              break;
            case 'most_viewed':
              comparison = b.views - a.views;
              break;
            case 'most_liked':
              comparison = b.likes - a.likes;
              break;
            case 'title':
              comparison = a.title.localeCompare(b.title, 'ar');
              break;
          }
          return filters.sortOrder === 'desc' ? -comparison : comparison;
        });
      }

      return {
        items: topics,
        total: topics.length,
        page: 1,
        pageSize: topics.length,
        hasMore: false
      };
    } catch (error) {
      console.error('Error searching topics:', error);
      throw new Error('فشل في البحث');
    }
  }
}

/**
 * خدمات بدائل الكوفات
 */
export class CoilAlternativeService {
  // جلب جميع بدائل الكوفات
  static async getCoilAlternatives(page = 1, pageSize = 20): Promise<SearchResult<CoilAlternative>> {
    try {
      const q = query(
        collection(db, COILS_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      const snapshot = await getDocs(q);
      const coils = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as CoilAlternative[];

      return {
        items: coils,
        total: coils.length,
        page,
        pageSize,
        hasMore: coils.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching coil alternatives:', error);
      throw new Error('فشل في جلب بدائل الكوفات');
    }
  }

  // دالة مساعدة لمعالجة التواريخ
  private static parseDate(date: any): Date {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (typeof date.toDate === 'function') return date.toDate();
    return new Date(date);
  }

  // البحث عن كوف بالرقم أو جزء من اسم الصورة
  static async searchCoilByNumber(coilNumber: string): Promise<CoilSearchResult[]> {
    try {
      const results: CoilSearchResult[] = [];
      const searchTerm = coilNumber.toLowerCase().trim();

      // البحث المطابق تماماً
      const exactQuery = query(
        collection(db, COILS_COLLECTION),
        where('originalCoilNumber', '==', coilNumber)
      );
      const exactSnapshot = await getDocs(exactQuery);

      exactSnapshot.docs.forEach(doc => {
        results.push({
          coil: {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date()
          } as CoilAlternative,
          matchType: 'exact',
          confidence: 100
        });
      });

      // البحث في البدائل
      const alternativeQuery = query(
        collection(db, COILS_COLLECTION),
        where('alternativeCoilNumber', '==', coilNumber)
      );
      const alternativeSnapshot = await getDocs(alternativeQuery);

      alternativeSnapshot.docs.forEach(doc => {
        results.push({
          coil: {
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date()
          } as CoilAlternative,
          matchType: 'alternative',
          confidence: 90
        });
      });

      // البحث الجزئي في جميع الحقول
      const allCoilsQuery = query(
        collection(db, COILS_COLLECTION),
        orderBy('createdAt', 'desc'),
        limit(100) // جلب المزيد من النتائج للبحث الجزئي
      );
      const allCoilsSnapshot = await getDocs(allCoilsQuery);

      allCoilsSnapshot.docs.forEach(doc => {
        const coilData = doc.data() as CoilAlternative;
        const originalCoil = coilData.originalCoilNumber?.toLowerCase() || '';
        const alternativeCoil = coilData.alternativeCoilNumber?.toLowerCase() || '';
        const notes = coilData.notes?.toLowerCase() || '';

        // البحث في الملاحظات (التي قد تحتوي على اسم الصورة)
        if (notes.includes(searchTerm)) {
          const existingResult = results.find(r => r.coil.id === doc.id);
          if (!existingResult) {
            results.push({
              coil: {
                ...coilData,
                id: doc.id,
                createdAt: CoilAlternativeService.parseDate(coilData.createdAt),
                updatedAt: CoilAlternativeService.parseDate(coilData.updatedAt)
              } as CoilAlternative,
              matchType: 'description',
              confidence: 85
            });
          }
        }

        // البحث الجزئي في الرقم الأصلي
        if (originalCoil.includes(searchTerm) && !originalCoil.startsWith(searchTerm)) {
          const existingResult = results.find(r => r.coil.id === doc.id);
          if (!existingResult) {
            results.push({
              coil: {
                ...coilData,
                id: doc.id,
                createdAt: CoilAlternativeService.parseDate(coilData.createdAt),
                updatedAt: CoilAlternativeService.parseDate(coilData.updatedAt)
              } as CoilAlternative,
              matchType: 'partial_original',
              confidence: 70
            });
          }
        }

        // البحث الجزئي في الرقم البديل
        if (alternativeCoil.includes(searchTerm) && !alternativeCoil.startsWith(searchTerm)) {
          const existingResult = results.find(r => r.coil.id === doc.id);
          if (!existingResult) {
            results.push({
              coil: {
                ...coilData,
                id: doc.id,
                createdAt: CoilAlternativeService.parseDate(coilData.createdAt),
                updatedAt: CoilAlternativeService.parseDate(coilData.updatedAt)
              } as CoilAlternative,
              matchType: 'partial_alternative',
              confidence: 65
            });
          }
        }

        // البحث في وصف الصور
        coilData.images.forEach(image => {
          if (image.description?.toLowerCase().includes(searchTerm)) {
            const existingResult = results.find(r => r.coil.id === doc.id);
            if (!existingResult) {
              results.push({
                coil: {
                  ...coilData,
                  id: doc.id,
                  createdAt: CoilAlternativeService.parseDate(coilData.createdAt),
                  updatedAt: CoilAlternativeService.parseDate(coilData.updatedAt)
                } as CoilAlternative,
                matchType: 'image_name',
                confidence: 80
              });
            }
          }
        });
      });

      // ترتيب النتائج حسب الثقة
      results.sort((a, b) => b.confidence - a.confidence);

      return results;
    } catch (error) {
      console.error('Error searching coil:', error);
      throw new Error('فشل في البحث عن الكوف');
    }
  }

  // إضافة بديل كوف جديد
  static async addCoilAlternative(coil: Omit<CoilAlternative, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, COILS_COLLECTION), {
        ...coil,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding coil alternative:', error);
      throw new Error('فشل في إضافة بديل الكوف');
    }
  }

  // تحديث بديل كوف
  static async updateCoilAlternative(id: string, updates: Partial<CoilAlternative>): Promise<void> {
    try {
      await updateDoc(doc(db, COILS_COLLECTION, id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating coil alternative:', error);
      throw new Error('فشل في تحديث بديل الكوف');
    }
  }

  // حذف بديل كوف
  static async deleteCoilAlternative(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COILS_COLLECTION, id));
    } catch (error) {
      console.error('Error deleting coil alternative:', error);
      throw new Error('فشل في حذف بديل الكوف');
    }
  }

  // جلب بديل كوف واحد
  static async getCoilAlternative(id: string): Promise<CoilAlternative | null> {
    try {
      const docRef = doc(db, COILS_COLLECTION, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data()?.createdAt?.toDate() || new Date(),
        updatedAt: docSnap.data()?.updatedAt?.toDate() || new Date()
      } as CoilAlternative;
    } catch (error) {
      console.error('Error fetching coil alternative:', error);
      throw new Error('فشل في جلب بديل الكوف');
    }
  }
}

/**
 * خدمات الإحصائيات
 */
export class MaintenanceBankStatsService {
  static async getStats(): Promise<MaintenanceBankStats> {
    try {
      // جلب عدد الفئات
      const categoriesSnapshot = await getDocs(collection(db, CATEGORIES_COLLECTION));
      const totalCategories = categoriesSnapshot.size;

      // جلب عدد المواضيع
      const topicsSnapshot = await getDocs(collection(db, TOPICS_COLLECTION));
      const totalTopics = topicsSnapshot.size;

      // جلب عدد بدائل الكوفات
      const coilsSnapshot = await getDocs(collection(db, COILS_COLLECTION));
      const totalCoilAlternatives = coilsSnapshot.size;

      // حساب إجمالي المشاهدات والإعجابات
      let totalViews = 0;
      let totalLikes = 0;
      const topics = topicsSnapshot.docs.map(doc => doc.data() as FaultTopic);

      topics.forEach(topic => {
        totalViews += topic.views || 0;
        totalLikes += topic.likes || 0;
      });

      // أكثر المواضيع مشاهدة
      const mostViewedTopics = topics
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 5)
        .map(topic => ({
          ...topic,
          createdAt: new Date(),
          updatedAt: new Date()
        })) as FaultTopic[];

      return {
        totalCategories,
        totalTopics,
        totalCoilAlternatives,
        totalViews,
        totalLikes,
        mostViewedTopics,
        mostSearchedCoils: [], // يمكن تنفيذها لاحقاً
        recentActivity: [] // يمكن تنفيذها لاحقاً
      };
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw new Error('فشل في جلب الإحصائيات');
    }
  }
}
